import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { BookOpen, Sparkles, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const MAX_PAGE_COUNT = 5000;
const LARGE_PAGE_WARNING = 1000;
const LARGE_CHANGE_THRESHOLD = 500;

interface UpdateReadingProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookId: string;
  userBookId: string;
  currentPage: number;
  totalPages: number;
  bookTitle: string;
  onProgressUpdated: () => void;
}

export function UpdateReadingProgressDialog({
  open,
  onOpenChange,
  bookId,
  userBookId,
  currentPage: initialCurrentPage,
  totalPages: initialTotalPages,
  bookTitle,
  onProgressUpdated
}: UpdateReadingProgressDialogProps) {
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);

  useEffect(() => {
    setCurrentPage(initialCurrentPage);
    setTotalPages(initialTotalPages);
  }, [initialCurrentPage, initialTotalPages, open]);

  const progress = totalPages > 0 ? Math.min(100, Math.round((currentPage / totalPages) * 100)) : 0;

  const handleQuickAdd = (pages: number) => {
    const newPage = Math.min(totalPages, currentPage + pages);
    setCurrentPage(newPage);
  };

  const handleSave = async () => {
    if (currentPage < 0 || currentPage > totalPages) {
      toast({
        variant: "destructive",
        title: "Invalid page number",
        description: `Page must be between 0 and ${totalPages}`,
      });
      return;
    }

    if (totalPages > MAX_PAGE_COUNT) {
      toast({
        variant: "destructive",
        title: "Invalid page count",
        description: `Page count cannot exceed ${MAX_PAGE_COUNT}`,
      });
      return;
    }

    // Check if this is a large change from initial value
    const pageCountChange = Math.abs(totalPages - initialTotalPages);
    if (pageCountChange >= LARGE_CHANGE_THRESHOLD && !pendingSave) {
      setPendingSave(true);
      setShowConfirmDialog(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('user_books')
        .update({
          current_page: currentPage,
          total_pages: totalPages,
        })
        .eq('id', userBookId);

      if (error) throw error;

      // Update streak for meaningful reading activity
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        supabase.functions.invoke('update-streak', {
          body: { userId: userData.user.id }
        }).catch(err => console.error('Error updating streak:', err));
      }

      // Show encouraging message based on progress
      let message = 'Progress updated!';
      if (currentPage === totalPages) {
        message = '🎉 Congratulations! You finished the book!';
      } else if (progress >= 75) {
        message = '🔥 Almost there! Keep going!';
      } else if (progress >= 50) {
        message = '📚 Halfway through! Great progress!';
      } else if (progress >= 25) {
        message = '✨ Making great progress!';
      }

      toast({
        title: message,
        description: `You're at page ${currentPage} of ${totalPages} (${progress}%)`,
      });

      onProgressUpdated();
      onOpenChange(false);
      setPendingSave(false);
    } catch (error) {
      console.error('Error updating progress:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update reading progress.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLargeChange = () => {
    setShowConfirmDialog(false);
    performSave();
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setPendingSave(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Update Reading Progress
            </DialogTitle>
            <DialogDescription>
              Track your progress in "{bookTitle}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Current Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Current Progress</span>
                <span className="text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} className="h-3" />
            </div>

            {/* Quick Add Buttons */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Quick Add</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdd(10)}
                  disabled={currentPage >= totalPages}
                >
                  +10 pages
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdd(25)}
                  disabled={currentPage >= totalPages}
                >
                  +25 pages
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdd(50)}
                  disabled={currentPage >= totalPages}
                >
                  +50 pages
                </Button>
              </div>
            </div>

            {/* Page Input */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current-page">Current Page</Label>
                <Input
                  id="current-page"
                  type="number"
                  min={0}
                  max={totalPages}
                  value={currentPage || ''}
                  onChange={(e) => setCurrentPage(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setCurrentPage(Math.min(totalPages, Math.max(0, val)));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-pages">Total Pages</Label>
                <Input
                  id="total-pages"
                  type="number"
                  min={0}
                  max={MAX_PAGE_COUNT}
                  value={totalPages || ''}
                  onChange={(e) => setTotalPages(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setTotalPages(Math.min(MAX_PAGE_COUNT, Math.max(1, val)));
                  }}
                />
              </div>
            </div>

            {/* Warning for large page counts */}
            {totalPages > LARGE_PAGE_WARNING && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This is a very large page count ({totalPages} pages). Please verify this is correct for your edition.
                  {totalPages > 2000 && " Books over 2,000 pages are usually collections or box sets."}
                </AlertDescription>
              </Alert>
            )}

            {/* Milestone Messages */}
            {progress >= 25 && progress < 100 && (
              <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg text-sm">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>
                  {progress >= 75 ? "Almost done! Just a bit more to go!" :
                   progress >= 50 ? "You're halfway there! Keep it up!" :
                   "Great start! Keep the momentum going!"}
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : 'Save Progress'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Large Page Count Change</AlertDialogTitle>
            <AlertDialogDescription>
              You're changing the page count from {initialTotalPages} to {totalPages} pages 
              (a change of {Math.abs(totalPages - initialTotalPages)} pages). 
              This is a significant change. Are you sure this is correct for your edition of the book?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLargeChange}>
              Yes, Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
